import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "~/shared/api/client";
import { projectsKeys, type ProjectGenerationListResponse, type ProjectLogResponse } from "./api-types";

// ---------------------------------------------------------------------------
// Fetch functions — activity log, generations, invite acceptance
// ---------------------------------------------------------------------------

export async function fetchProjectLog(projectId: string): Promise<ProjectLogResponse> {
    return apiGet<ProjectLogResponse>(`/v2/projects/${projectId}/log`);
}

export interface FetchProjectGenerationsParams {
    limit?: number;
    offset?: number;
}

export async function fetchProjectGenerations(
    projectId: string,
    params: FetchProjectGenerationsParams = {},
): Promise<ProjectGenerationListResponse> {
    return apiGet<ProjectGenerationListResponse>(`/v2/projects/${projectId}/generations`, {
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
    });
}

export async function acceptProjectInvite(token: string): Promise<{ projectId: string }> {
    return apiPost<{ projectId: string }>(`/v2/projects/invites/${encodeURIComponent(token)}/accept`, {});
}

// ---------------------------------------------------------------------------
// Query + mutation hooks
// ---------------------------------------------------------------------------

export function useProjectLog(projectId: string | undefined) {
    return useQuery<ProjectLogResponse, Error>({
        queryKey: projectId ? projectsKeys.log(projectId) : ["projects", "log", "skip"],
        queryFn: () => fetchProjectLog(projectId!),
        enabled: Boolean(projectId),
    });
}

export function useProjectGenerations(projectId: string | undefined, limit = 50, offset = 0) {
    return useQuery<ProjectGenerationListResponse, Error>({
        queryKey: projectId
            ? projectsKeys.generations(projectId, { limit, offset })
            : ["projects", "generations", "skip"],
        queryFn: () => fetchProjectGenerations(projectId!, { limit, offset }),
        enabled: Boolean(projectId),
        placeholderData: keepPreviousData,
    });
}

export function useAcceptProjectInvite() {
    return useMutation<{ projectId: string }, Error, string>({
        mutationFn: (token: string) => acceptProjectInvite(token),
    });
}
